import { useContext, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

import { FeedDensityContext, isDenseFeed } from './feedDensity';
import { FeedIcon } from './FeedIcon';
import { FeedOpenLink } from './FeedOpenLink';
import {
  FEED_PILL_CLASS,
  FEED_ROW_GRID,
  FEED_ROW_LINE_CLASS,
  FEED_ROW_TITLE_CLASS,
} from './feedStyles';

import type { HistoryRowView } from './historyRowView';
type HistoryRowProps = {
  view: HistoryRowView;
  icon: ReactNode;
  appName: string;
  // A note only staff see, such as the backend that made the card; it follows the meta
  staffLabel?: ReactNode;
  restoring: boolean;
  onOpen: () => void;
  onRestore: () => void;
  onOpenUrl: (url: string) => void;
};

// A card that left the Feed, in the Feed row's geometry; its meta is how it ended
export const HistoryRow = ({
  view,
  icon,
  appName,
  staffLabel,
  restoring,
  onOpen,
  onRestore,
  onOpenUrl,
}: HistoryRowProps) => {
  const compact = isDenseFeed(useContext(FeedDensityContext));
  return (
    <div
      data-testid="history-row"
      className={cn(
        FEED_ROW_GRID,
        'group/feed-row rounded-[calc(10*var(--ac-u))] transition-colors duration-[120ms] hover:bg-surface-hover has-[:focus-visible]:bg-surface-hover motion-reduce:transition-none'
      )}
    >
      <FeedIcon failed={false}>{icon}</FeedIcon>
      <span className={compact ? FEED_ROW_LINE_CLASS : 'flex min-w-0 flex-col'}>
        <button
          type="button"
          aria-expanded={false}
          className={FEED_ROW_TITLE_CLASS}
          onClick={onOpen}
        >
          {view.title}
        </button>
        <span
          data-testid="history-row-meta"
          className={cn(
            'min-w-0 truncate text-[length:calc(13*var(--ac-u))] leading-[calc(18*var(--ac-u))]',
            compact && 'shrink-[100]',
            view.outcome === 'done' ? 'text-kind-done' : 'text-foreground-muted'
          )}
        >
          {view.meta}
        </span>
        {staffLabel}
      </span>
      <span className="relative z-[1] flex items-center gap-[calc(6*var(--ac-u))]">
        {view.openUrl ? (
          <FeedOpenLink
            url={view.openUrl}
            appName={appName}
            onOpenUrl={onOpenUrl}
          />
        ) : null}
        {view.canRestore ? (
          <Button
            variant="outline"
            size="xs"
            isLoading={restoring}
            loadingText={view.restoreLabel}
            className={cn(
              FEED_PILL_CLASS,
              !restoring &&
                'hidden group-hover/feed-row:grid group-has-[:focus-visible]/feed-row:grid'
            )}
            onClick={onRestore}
          >
            {view.restoreLabel}
          </Button>
        ) : null}
      </span>
    </div>
  );
};
